'use client';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { ARCHITECTURE_VERSION, ArchitectureFilter, ArchitectureVersion, MODEL_SIZE, FORMAT, GEAR, isGearFormatCompatible } from '@/utils/constants';
import Checkbox from '../Checkbox/Checkbox';
import { dashToSpaceUpperFirst } from '@/utils/helpers';
import {
  getSearchTagsAggregate,
  getSearchMakesAggregate,
  getSearchCreatorsAggregate,
} from '@/utils/supabase/queries';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';
import { orderSearchParams } from '@/utils/search/params';
import { useEffect } from 'react';
import { createClient } from '@/utils/supabase/client';
import Toggle from '../Toggle/Toggle';
import ProfileAvatar from '../ProfileUser/ProfileAvatar';
import { Varified } from '@/components/icons/Varified';

const FilterIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M3 6.00012H21"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7 12.0001H17"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10 18.0001H14"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface ExpandCollapseProps {
  title: string;
  children: React.ReactNode;
  isExpanded: boolean;
  onExpandChange: (expanded: boolean) => void;
  enabled?: boolean;
  disabled?: boolean;
}

const Expand = ({
  title,
  children,
  isExpanded,
  onExpandChange,
  enabled = false,
  disabled = false,
}: ExpandCollapseProps) => {
  const effectivelyExpanded = isExpanded && !disabled;
  return (
    <div
      className={`rounded-lg border ${effectivelyExpanded ? ' border-white' : ' border-zinc-700'} ${disabled ? 'opacity-50' : ''}`}
    >
      <button
        className="w-full px-4 py-3 flex items-center justify-between font-semibold focus:outline-none disabled:cursor-not-allowed"
        onClick={() => onExpandChange(!isExpanded)}
        disabled={disabled}
      >
        <div className="flex flex-row gap-2 items-center">
          <span className="font-medium text-lg text-left">{title}</span>
          {enabled && <span className="bg-white rounded-full h-2 w-2"></span>}
        </div>

        {effectivelyExpanded ? (
          <ChevronUp className="flex-shrink-0" size={24} />
        ) : (
          <ChevronDown className="flex-shrink-0" size={24} />
        )}
      </button>
      <div className={`px-4 border-t border-white w-full ${effectivelyExpanded ? 'block' : 'hidden'}`}>
        {effectivelyExpanded && children}
      </div>
    </div>
  );
};

interface FilterSheetProps {
  children: React.ReactNode;
  mobileOpen: boolean;
  onMobileOpenChange: (open: boolean) => void;
  mobileTitle?: string;
  onReset?: () => void;
  hasUserAppliedFilters?: boolean;
}

const FilterSheet = ({ children, mobileOpen, onMobileOpenChange, mobileTitle, onReset, hasUserAppliedFilters }: FilterSheetProps) => {
  const t = useTranslations('search.filters');
  return (
    <div>
      {/* Mobile full-page overlay */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 bg-black flex flex-col">
          <div className="flex items-center justify-between px-4 py-6 flex-shrink-0">
            <div className="flex flex-row items-center gap-4">
              <FilterIcon />
              <h2 className="text-xl font-bold">{mobileTitle ?? t('title')}</h2>
            </div>
            <button onClick={() => onMobileOpenChange(false)} className="p-1">
              <X size={24} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-4">
            {children}
          </div>
          <div className="px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] flex-shrink-0 flex flex-row gap-3">
            <button
              onClick={() => { onReset?.(); }}
              className="px-6 py-3 border border-zinc-700 text-white rounded-xl"
            >
              {t('reset')}
            </button>
            <button
              onClick={() => onMobileOpenChange(false)}
              disabled={!hasUserAppliedFilters}
              className="flex-1 py-3 text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: 'linear-gradient(180deg, #00f 0%, #0000d0 100%)' }}
            >
              {t('apply')}
            </button>
          </div>
        </div>
      )}

      {/* Desktop: Just render children without wrapper */}
      <div className="hidden md:block">{children}</div>
    </div>
  );
};

const TagFilter = ({
  superPlatform,
  selectArchitecture,
  lockCalibrated,
  isExpanded,
  onExpandChange,
}: {
  superPlatform?: FORMAT;
  selectArchitecture?: ArchitectureFilter;
  lockCalibrated?: boolean;
  isExpanded: boolean;
  onExpandChange: (expanded: boolean) => void;
}) => {
  const pageSize = 10;
  const t = useTranslations('search');
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const query = searchParams.get('q');
  const tags = searchParams.get('tags')?.split('_') || null;
  const gears = searchParams.get('gears')?.split('_') || null;
  const makes = searchParams.get('makes')?.split('_') || null;
  const calibrated = !!lockCalibrated || searchParams.get('calibrated') === 'true';
  const verified = searchParams.get('verified') === 'true';
  const sizes = (searchParams.get('sizes')?.split(/[_-]/) as MODEL_SIZE[]) || null;
  const creators = searchParams.get('creators')?.split('-') || null;
  const [selectedTags, setSelectedTags] = useState<string[]>(tags || []);
  const [tagCounts, setTagCounts] = useState<{ name: string; count: number; id: number }[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const supabase = createClient();
  const [tagSearch, setTagSearch] = useState('');
  const [tagSearchResults, setTagSearchResults] = useState<{ name: string; id: number }[]>([]);
  const [tagSearchLoading, setTagSearchLoading] = useState(false);

  const resetSearch = () => {
    setTagSearchResults([]);
    setTagSearchLoading(false);
    setTagSearch('');
  };

  useEffect(() => {
    if (tags?.toString() === selectedTags?.toString()) return;
    setSelectedTags(tags || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags?.toString()]);

  const deps = [
    query?.toString(),
    gears?.toString(),
    tags?.toString(),
    makes?.toString(),
    calibrated?.toString(),
    verified?.toString(),
    sizes?.toString(),
    creators?.toString(),
    selectArchitecture?.toString(),
  ];

  useEffect(() => {
    const fetchAggregations = async () => {
      const aggregate = await getSearchTagsAggregate(supabase, {
        query: query || '',
        page: 1,
        pageSize,
        tags,
        makes,
        gears,
        calibrated,
        sizes,
        creators,
        platform: superPlatform,
        architecture: selectArchitecture,
        verifiedOnly: verified,
      });
      resetSearch();
      setTagCounts(aggregate.tag_counts || []);
      setTotalCount(aggregate.total_count || 0);
      setPage(1);
    };
    fetchAggregations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const loadMoreTags = async () => {
    const aggregate = await getSearchTagsAggregate(supabase, {
      query: query || '',
      page: page + 1,
      pageSize,
      tags,
      makes,
      gears,
      calibrated,
      sizes,
      creators,
      platform: superPlatform,
      architecture: selectArchitecture,
      verifiedOnly: verified,
    });
    setTagCounts((prev) => [...prev, ...(aggregate.tag_counts || [])]);
    setTotalCount(aggregate.total_count || 0);
    setPage(page + 1);
  };

  const onChange = (tagName: string, checked: boolean) => {
    const newSelectedTags = checked
      ? [...selectedTags, tagName]
      : selectedTags.filter((t) => t !== tagName);
    const newParams = new URLSearchParams(searchParams);
    if (newSelectedTags.length === 0) {
      newParams.delete('tags');
    } else {
      newParams.set('tags', newSelectedTags.join('_'));
    }
    // remove pagination param
    newParams.delete('page');
    setSelectedTags(newSelectedTags);
    router.push(`${pathname}?${orderSearchParams(newParams)}`, { scroll: false });
  };

  const searchTags = (search: string) => {
    setTagSearch(search);
    if (search.length === 0) {
      setTagSearchResults([]);
      return;
    }
    const fetch = async () => {
      setTagSearchLoading(true);
      const { data, error } = await supabase.rpc('get_popular_tags', {
        search_term: search,
        result_limit: 15,
      });
      if (error) {
        console.error(error);
      } else {
        // Same shape as the aggregate above: one row holding the counts.
        setTagSearchResults(
          (data?.[0]?.tag_counts || []) as unknown as { name: string; id: number }[]
        );
      }
      setTagSearchLoading(false);
    };
    fetch();
  };

  // if (totalCount === 0) return null;
  const tagsToDisplay =
    tagSearch.length > 0 && (!tagSearchLoading || tagSearchResults.length > 0)
      ? tagSearchResults
      : tagCounts;

  return (
    <Expand
      title={t('filters.tags')}
      enabled={selectedTags.length > 0}
      isExpanded={isExpanded}
      onExpandChange={onExpandChange}
    >
      <div className="flex flex-col gap-4 py-4">
        <div className="w-full md:flex-1 relative flex">
          <input
            type="text"
            placeholder={t('searchPlaceholder')}
            className="w-full bg-zinc-900 outline-none rounded-full border border-zinc-700 pl-4 pr-10 min-h-[38px] flex-1"
            value={tagSearch}
            onChange={(e) => searchTags(e.target.value)}
          />
          {tagSearch.length > 0 && (
            <button
              onClick={() => searchTags('')}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-zinc-400 hover:text-zinc-300"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
        <div className="flex flex-row gap-2 flex-wrap">
          {tagsToDisplay.map((tag) => (
            <button
              key={tag.id}
              className={`px-4 py-2 text-sm flex flex-row items-center border rounded-full ${
                selectedTags.includes(tag.name) ? 'border-white' : 'border-zinc-700'
              }`}
              onClick={() => onChange(tag.name, !selectedTags.includes(tag.name))}
            >
              {tag.name}
            </button>
          ))}
        </div>
        {tagSearch.length === 0 && totalCount > tagCounts.length + 1 && (
          <button
            className={
              'text-white flex gap-2 items-center border border-white rounded-lg px-3 py-1 w-max'
            }
            onClick={loadMoreTags}
          >
            {t('filters.showMore')}
          </button>
        )}
      </div>
    </Expand>
  );
};

const MakeFilter = ({
  superPlatform,
  selectArchitecture,
  lockCalibrated,
  isExpanded,
  onExpandChange,
}: {
  superPlatform?: FORMAT;
  selectArchitecture?: ArchitectureFilter;
  lockCalibrated?: boolean;
  isExpanded: boolean;
  onExpandChange: (expanded: boolean) => void;
}) => {
  const pageSize = 10;
  const t = useTranslations('search');
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const query = searchParams.get('q');
  const gears = searchParams.get('gears')?.split('_') || null;
  const tags = searchParams.get('tags')?.split('_') || null;
  const makes = searchParams.get('makes')?.split('_') || null;
  const calibrated = !!lockCalibrated || searchParams.get('calibrated') === 'true';
  const verified = searchParams.get('verified') === 'true';
  const sizes = (searchParams.get('sizes')?.split(/[_-]/) as MODEL_SIZE[]) || null;
  const creators = searchParams.get('creators')?.split('-') || null;
  const [selectedMakes, setSelectedMakes] = useState<string[]>(makes || []);
  const [makeCounts, setMakeCounts] = useState<{ name: string; count: number; id: number }[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const supabase = createClient();
  const [makeSearch, setMakeSearch] = useState('');
  const [makeSearchResults, setMakeSearchResults] = useState<{ name: string; id: number }[]>([]);
  const [makeSearchLoading, setMakeSearchLoading] = useState(false);

  const resetSearch = () => {
    setMakeSearchResults([]);
    setMakeSearchLoading(false);
    setMakeSearch('');
  };

  useEffect(() => {
    if (makes?.toString() === selectedMakes?.toString()) return;
    setSelectedMakes(makes || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [makes?.toString()]);

  const deps = [
    query?.toString(),
    gears?.toString(),
    tags?.toString(),
    makes?.toString(),
    calibrated?.toString(),
    verified?.toString(),
    sizes?.toString(),
    creators?.toString(),
    selectArchitecture?.toString(),
  ];

  useEffect(() => {
    const fetchAggregations = async () => {
      const aggregate = await getSearchMakesAggregate(supabase, {
        query: query || '',
        page: 1,
        pageSize,
        tags,
        makes,
        gears,
        calibrated,
        sizes,
        creators,
        platform: superPlatform,
        architecture: selectArchitecture,
        verifiedOnly: verified,
      });
      resetSearch();
      setMakeCounts(aggregate.make_counts || []);
      setTotalCount(aggregate.total_count || 0);
      setPage(1);
    };
    fetchAggregations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const onChange = (makeName: string, checked: boolean) => {
    const newSelectedMakes = checked
      ? [...selectedMakes, makeName]
      : selectedMakes.filter((m) => m !== makeName);
    const newParams = new URLSearchParams(searchParams);
    if (newSelectedMakes.length === 0) {
      newParams.delete('makes');
    } else {
      newParams.set('makes', newSelectedMakes.join('_'));
    }
    // remove pagination param
    newParams.delete('page');
    setSelectedMakes(newSelectedMakes);
    router.push(`${pathname}?${orderSearchParams(newParams)}`, { scroll: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  const loadMoreMakes = async () => {
    const aggregate = await getSearchMakesAggregate(supabase, {
      query: query || '',
      page: page + 1,
      pageSize,
      tags,
      makes,
      gears,
      calibrated,
      sizes,
      creators,
      platform: superPlatform,
      architecture: selectArchitecture,
      verifiedOnly: verified,
    });
    setMakeCounts((prev) => [...prev, ...(aggregate.make_counts || [])]);
    setTotalCount(aggregate.total_count || 0);
    setPage(page + 1);
  };

  const searchMakes = (search: string) => {
    setMakeSearch(search);
    if (search.length === 0) {
      setMakeSearchResults([]);
      return;
    }
    const fetch = async () => {
      setMakeSearchLoading(true);
      const { data, error } = await supabase.rpc('get_popular_makes', {
        search_term: search,
        result_limit: 15,
      });

      if (error) {
        console.error(error);
      } else {
        setMakeSearchResults(
          (data?.[0]?.make_counts || []) as unknown as { name: string; id: number }[]
        );
      }
      setMakeSearchLoading(false);
    };
    fetch();
  };

  // if (totalCount === 0) return null;
  const makesToDisplay =
    makeSearch.length > 0 && (!makeSearchLoading || makeSearchResults.length > 0)
      ? makeSearchResults
      : makeCounts;

  return (
    <Expand
      title={t('filters.makesAndModels')}
      enabled={selectedMakes.length > 0}
      isExpanded={isExpanded}
      onExpandChange={onExpandChange}
    >
      <div className="flex flex-col gap-4 py-4">
        <div className="w-full md:flex-1 relative flex">
          <input
            type="text"
            placeholder={t('searchPlaceholder')}
            className="w-full bg-zinc-900 outline-none rounded-full border border-zinc-700 pl-4 pr-10 min-h-[38px] flex-1"
            value={makeSearch}
            onChange={(e) => searchMakes(e.target.value)}
          />
          {makeSearch.length > 0 && (
            <button
              onClick={() => searchMakes('')}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-zinc-400 hover:text-zinc-300"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
        {makesToDisplay.map((make) => (
          <Checkbox
            key={make.name}
            label={make.name}
            checked={selectedMakes.includes(make.name)}
            onChange={(checked) => onChange(make.name, checked)}
            style={selectedMakes.includes(make.name) ? { color: 'white' } : {}}
          />
        ))}
        {makeSearch.length === 0 && totalCount > makeCounts.length + 1 && (
          <button
            className={
              'text-white flex gap-2 items-center border border-white rounded-lg px-3 py-1 w-max'
            }
            onClick={loadMoreMakes}
          >
            {t('filters.showMore')}
          </button>
        )}
      </div>
    </Expand>
  );
};

const CreatorFilter = ({
  superPlatform,
  selectArchitecture,
  lockCalibrated,
  isExpanded,
  onExpandChange,
}: {
  superPlatform?: FORMAT;
  selectArchitecture?: ArchitectureFilter;
  lockCalibrated?: boolean;
  isExpanded: boolean;
  onExpandChange: (expanded: boolean) => void;
}) => {
  const pageSize = 10;
  const t = useTranslations('search');
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const query = searchParams.get('q');
  const gears = searchParams.get('gears')?.split('_') || null;
  const tags = searchParams.get('tags')?.split('_') || null;
  const makes = searchParams.get('makes')?.split('_') || null;
  const creators = searchParams.get('creators')?.split('-') || null;
  const calibrated = !!lockCalibrated || searchParams.get('calibrated') === 'true';
  const verified = searchParams.get('verified') === 'true';
  const sizes = (searchParams.get('sizes')?.split(/[_-]/) as MODEL_SIZE[]) || null;
  const [selectedCreators, setSelectedCreators] = useState<string[]>(creators || []);
  const [creatorCounts, setCreatorCounts] = useState<
    { username: string; avatar_url: string; count: number }[]
  >([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const supabase = createClient();
  const [creatorSearch, setCreatorSearch] = useState('');
  const [creatorSearchResults, setCreatorSearchResults] = useState<
    { username: string; avatar_url: string }[]
  >([]);
  const [creatorSearchLoading, setCreatorSearchLoading] = useState(false);

  const resetSearch = () => {
    setCreatorSearchResults([]);
    setCreatorSearchLoading(false);
    setCreatorSearch('');
  };

  useEffect(() => {
    if (creators?.toString() === selectedCreators?.toString()) return;
    setSelectedCreators(creators || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creators?.toString()]);

  const deps = [
    query?.toString(),
    gears?.toString(),
    tags?.toString(),
    makes?.toString(),
    creators?.toString(),
    calibrated?.toString(),
    verified?.toString(),
    sizes?.toString(),
    selectArchitecture?.toString(),
  ];

  useEffect(() => {
    const fetchAggregations = async () => {
      const aggregate = await getSearchCreatorsAggregate(supabase, {
        query: query || '',
        page: 1,
        pageSize,
        tags,
        makes,
        gears,
        calibrated,
        sizes,
        creators,
        platform: superPlatform,
        architecture: selectArchitecture,
        verifiedOnly: verified,
      });
      resetSearch();
      setCreatorCounts(aggregate.username_counts || []);
      setTotalCount(aggregate.total_count || 0);
      setPage(1);
    };
    fetchAggregations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const loadMoreCreators = async () => {
    const aggregate = await getSearchCreatorsAggregate(supabase, {
      query: query || '',
      page: page + 1,
      pageSize,
      tags,
      makes,
      gears,
      calibrated,
      sizes,
      creators,
      platform: superPlatform,
      architecture: selectArchitecture,
      verifiedOnly: verified,
    });
    setCreatorCounts((prev) => [...prev, ...(aggregate.username_counts || [])]);
    setTotalCount(aggregate.total_count || 0);
    setPage(page + 1);
  };

  const onChange = (username: string, checked: boolean) => {
    const newSelectedCreators = checked
      ? [...selectedCreators, username]
      : selectedCreators.filter((c) => c !== username);
    const newParams = new URLSearchParams(searchParams);
    if (newSelectedCreators.length === 0) {
      newParams.delete('creators');
    } else {
      newParams.set('creators', newSelectedCreators.join('-'));
    }
    // remove pagination param
    newParams.delete('page');
    setSelectedCreators(newSelectedCreators);
    router.push(`${pathname}?${orderSearchParams(newParams)}`, { scroll: false });
  };

  const onChangeVerified = (checked: boolean) => {
    const newParams = new URLSearchParams(searchParams);
    if (checked) {
      newParams.set('verified', 'true');
      // An unverified creator would keep filtering while its checkbox drops out
      // of the facet list, so the filter goes invisible but stays active.
      newParams.delete('creators');
      setSelectedCreators([]);
    } else {
      newParams.delete('verified');
    }
    // remove pagination param
    newParams.delete('page');
    router.push(`${pathname}?${orderSearchParams(newParams)}`, { scroll: false });
  };

  const searchCreators = (search: string) => {
    setCreatorSearch(search);
    if (search.length === 0) {
      setCreatorSearchResults([]);
      return;
    }
    const fetch = async () => {
      setCreatorSearchLoading(true);
      // This box goes straight to the view rather than the aggregate RPC, so
      // the verified filter has to be reapplied here by hand.
      let creatorQuery = supabase
        .from('user_public_counts')
        .select('username, avatar_url')
        .gt('public_models_count', 0)
        .ilike('username', `%${search}%`);
      if (verified) {
        creatorQuery = creatorQuery.eq('is_verified', true);
      }
      const { data, error } = await creatorQuery.limit(10);

      if (error) {
        console.error(error);
      } else {
        setCreatorSearchResults(
          data.map((user) => ({ username: user.username || '', avatar_url: user.avatar_url || '' }))
        );
      }
      setCreatorSearchLoading(false);
    };
    fetch();
  };

  // if (totalCount === 0) return null;
  const creatorsToDisplay =
    creatorSearch.length > 0 && (!creatorSearchLoading || creatorSearchResults.length > 0)
      ? creatorSearchResults
      : creatorCounts;

  return (
    <Expand
      title={t('filters.creators')}
      enabled={selectedCreators.length > 0 || verified}
      isExpanded={isExpanded}
      onExpandChange={onExpandChange}
    >
      <div className="flex flex-col gap-4 py-4">
        <Toggle
          name={
            <span className="flex flex-row items-center gap-2">
              <Varified size={20} className="flex-shrink-0" />
              Verified
            </span>
          }
          label=""
          isChecked={verified}
          onChange={onChangeVerified}
        />
        <div className="w-full md:flex-1 relative flex">
          <input
            type="text"
            placeholder={t('searchPlaceholder')}
            className="w-full bg-zinc-900 outline-none rounded-full border border-zinc-700 pl-4 pr-10 min-h-[38px] flex-1"
            value={creatorSearch}
            onChange={(e) => searchCreators(e.target.value)}
          />
          {creatorSearch.length > 0 && (
            <button
              onClick={() => searchCreators('')}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-zinc-400 hover:text-zinc-300"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
        {creatorsToDisplay.map((creator) => (
          <Checkbox
            key={creator.username}
            label={
              <span className="flex flex-row items-center gap-2">
                <ProfileAvatar
                  avatarUrl={creator.avatar_url}
                  ownProfile={false}
                  username={creator.username}
                  size={24}
                />
                {creator.username}
              </span>
            }
            checked={selectedCreators.includes(creator.username)}
            onChange={(checked) => onChange(creator.username, checked)}
            style={selectedCreators.includes(creator.username) ? { color: 'white' } : {}}
          />
        ))}
        {creatorSearch.length === 0 && totalCount > creatorCounts.length + 1 && (
          <button
            className={
              'text-white flex gap-2 items-center border border-white rounded-lg px-3 py-1 w-max'
            }
            onClick={loadMoreCreators}
          >
            {t('filters.showMore')}
          </button>
        )}
      </div>
    </Expand>
  );
};

// Sizes shown under A1 — Custom is its own architecture, not an A1 size.
const A1_SIZE_OPTIONS = Object.values(MODEL_SIZE)
  .filter((size) => size !== MODEL_SIZE.CUSTOM)
  .map((size) => ({ label: dashToSpaceUpperFirst(size), value: size }));

// Label text lives in the message catalog (search.filters.architecture.*) so the
// version identifiers (A1/A2) stay inline while the descriptor words translate.
const ARCHITECTURE_OPTIONS: { value: ArchitectureVersion; labelKey: string }[] = [
  { value: ARCHITECTURE_VERSION.A2, labelKey: 'architecture.a2' },
  { value: ARCHITECTURE_VERSION.A1, labelKey: 'architecture.a1' },
  { value: ARCHITECTURE_VERSION.CUSTOM, labelKey: 'architecture.custom' },
];

const Radio = ({
  checked,
  disabled,
  label,
  onChange,
  className = '',
}: {
  checked: boolean;
  disabled?: boolean;
  label: React.ReactNode;
  onChange: () => void;
  className?: string;
}) => (
  <label
    className={`flex items-center gap-3 ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'} ${checked ? 'text-white' : 'text-zinc-400'} ${className}`}
  >
    <input
      type="radio"
      className="hidden"
      checked={checked}
      disabled={disabled}
      onChange={() => !disabled && onChange()}
    />
    <span
      className={`w-5 h-5 border-2 rounded-full flex items-center justify-center shrink-0 ${
        checked ? 'border-white' : 'border-zinc-600'
      }`}
    >
      {checked && <span className="w-3 h-3 rounded-full bg-white" />}
    </span>
    <span>{label}</span>
  </label>
);

const TechnicalFilter = ({
  selectArchitecture,
  flowLockedArchitecture,
  defaultArchitecture,
  lockCalibrated,
  isDisabled,
  isExpanded,
  onExpandChange,
}: {
  selectArchitecture?: ArchitectureFilter;
  flowLockedArchitecture?: ArchitectureFilter;
  /** Implicit default when nothing is selected (A2 on core, undefined for legacy partners). */
  defaultArchitecture?: ArchitectureFilter;
  /** Partner-locked calibrated filter (OAuth flow): forces it on, hides the toggle. */
  lockCalibrated?: boolean;
  isDisabled: boolean;
  isExpanded: boolean;
  onExpandChange: (expanded: boolean) => void;
}) => {
  const t = useTranslations('search.filters');
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const isLocked = !!flowLockedArchitecture;

  const urlSizes = searchParams.get('sizes')
    ? (searchParams.get('sizes')?.split(/[_-]/) as MODEL_SIZE[])
    : null;
  // Selected sizes follow the URL. Absent `?sizes=` = no size filter (all sizes
  // visible in results) AND empty checkboxes — user can click one to filter to
  // that single size.
  const effectiveSelectedSizes: MODEL_SIZE[] = urlSizes ?? [];

  const urlCalibrated = searchParams.get('calibrated') === 'true';
  const calibrated = !!lockCalibrated || urlCalibrated;
  // Locked calibrated is partner-driven, not a removable user filter, so it
  // doesn't activate the Technical section.
  const hasNonDefaultTechnical =
    selectArchitecture !== defaultArchitecture || urlCalibrated || !!urlSizes;

  const onChangeArchitecture = (next: ArchitectureVersion) => {
    if (isLocked || isDisabled) return;
    const newParams = new URLSearchParams(searchParams);
    if (next === defaultArchitecture) {
      newParams.delete('architecture');
    } else {
      newParams.set('architecture', next);
    }
    // Sizes only apply to A1 — clear when leaving A1.
    if (next !== ARCHITECTURE_VERSION.A1) {
      newParams.delete('sizes');
    }
    newParams.delete('page');
    router.push(`${pathname}?${orderSearchParams(newParams)}`, { scroll: false });
  };

  const onChangeSize = (size: MODEL_SIZE, checked: boolean) => {
    if (isDisabled) return;
    const next = checked
      ? [...effectiveSelectedSizes, size]
      : effectiveSelectedSizes.filter((s) => s !== size);
    const newParams = new URLSearchParams(searchParams);
    if (next.length === 0 || next.length === A1_SIZE_OPTIONS.length) {
      newParams.delete('sizes');
    } else {
      newParams.set('sizes', next.join('_'));
    }
    newParams.delete('page');
    router.push(`${pathname}?${orderSearchParams(newParams)}`, { scroll: false });
  };

  const onChangeCalibrated = (checked: boolean) => {
    if (isDisabled || lockCalibrated) return;
    const newParams = new URLSearchParams(searchParams);
    if (checked) {
      newParams.set('calibrated', 'true');
    } else {
      newParams.delete('calibrated');
    }
    newParams.delete('page');
    router.push(`${pathname}?${orderSearchParams(newParams)}`, { scroll: false });
  };

  return (
    <Expand
      title={t('technical')}
      enabled={hasNonDefaultTechnical && !isDisabled}
      disabled={isDisabled}
      isExpanded={isExpanded}
      onExpandChange={onExpandChange}
    >
      <div className={`flex flex-col gap-4 py-4 ${isDisabled ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="flex flex-col gap-3">
          {ARCHITECTURE_OPTIONS.map((opt) => {
            const isActive = selectArchitecture === opt.value;
            const radioDisabled = isDisabled || (isLocked && !isActive);
            return (
              <div key={opt.value} className="flex flex-col gap-3">
                <Radio
                  checked={isActive}
                  disabled={radioDisabled}
                  label={t(opt.labelKey)}
                  onChange={() => onChangeArchitecture(opt.value)}
                />
                {opt.value === ARCHITECTURE_VERSION.A1 && isActive && (
                  <div className="flex flex-col gap-3 pl-9">
                    {A1_SIZE_OPTIONS.map((sizeOpt) => {
                      const sizeChecked = effectiveSelectedSizes.includes(sizeOpt.value);
                      return (
                        <Checkbox
                          key={sizeOpt.value}
                          label={sizeOpt.label}
                          checked={sizeChecked}
                          onChange={(c) => onChangeSize(sizeOpt.value, c)}
                          style={sizeChecked ? { color: 'white' } : {}}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {/* Partner-locked calibrated filter: shown checked but disabled and
            dimmed (like the locked architecture radios) so the user sees the
            constraint without being able to widen past it. */}
        <div className={lockCalibrated && !isDisabled ? 'opacity-50' : ''}>
          <Toggle
            name="Calibrated"
            label=""
            isChecked={calibrated}
            onChange={onChangeCalibrated}
            disabled={isDisabled || lockCalibrated}
          />
        </div>
      </div>
    </Expand>
  );
};

// Format filter: standalone top-level drawer. NAM and IR are the only
// two options. NAM maps to `format=nam` ONLY — not the aida-x / aa-snapshot /
// proteus family (those rare formats are reachable only via no filter). One
// format at a time, so a radio group; "All" clears the param.
const FormatFilter = ({
  isExpanded,
  onExpandChange,
  disabled,
  lockedFormat,
  selectedGears,
}: {
  isExpanded: boolean;
  onExpandChange: (expanded: boolean) => void;
  /** When the flow (OAuth / embed) locks the Format, render read-only. */
  disabled?: boolean;
  /** The Format that the flow locked to, used to reflect the locked value. */
  lockedFormat?: FORMAT;
  /** Currently-selected gears. A Format unsupported by all of them is disabled. */
  selectedGears?: GEAR[];
}) => {
  const t = useTranslations('search.filters');
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const urlFormat = searchParams.get('format');
  const effective = disabled ? lockedFormat ?? null : urlFormat;
  const isNam = effective === FORMAT.NAM;
  const isIr = effective === FORMAT.IR;
  // A URL-only format (aida-x, proteus, …) selects nothing rather than "All".
  const isAll = effective == null;

  // A Format is offered only when at least one selected Gear supports it — e.g.
  // filtering by Amp + Cab disables IR. The currently-selected Format is never
  // disabled so it can always be cleared. We disable the unsupported Format
  // rather than deselecting the gear chip.
  const unsupportedByGears = (f: FORMAT) =>
    !!selectedGears?.length && !selectedGears.some((g) => isGearFormatCompatible(g, f));
  const namDisabled = disabled || (unsupportedByGears(FORMAT.NAM) && !isNam);
  const irDisabled = disabled || (unsupportedByGears(FORMAT.IR) && !isIr);

  const setFormat = (next: FORMAT | null) => {
    if (disabled) return;
    const newParams = new URLSearchParams(searchParams);
    if (next == null) {
      newParams.delete('format');
    } else {
      newParams.set('format', next);
    }
    // UI never writes the legacy alias
    newParams.delete('platform');
    newParams.delete('page');
    router.push(`${pathname}?${orderSearchParams(newParams)}`, { scroll: false });
  };

  return (
    <Expand
      title={t('format')}
      enabled={(isNam || isIr) && !disabled}
      disabled={disabled}
      isExpanded={isExpanded}
      onExpandChange={onExpandChange}
    >
      <div className={`flex flex-col gap-4 py-4 ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
        <Radio
          label={t('formatOptions.all')}
          checked={isAll}
          disabled={disabled}
          onChange={() => setFormat(null)}
        />
        <Radio
          label={t('formatOptions.nam')}
          checked={isNam}
          disabled={namDisabled}
          onChange={() => setFormat(FORMAT.NAM)}
        />
        <Radio
          label={t('formatOptions.ir')}
          checked={isIr}
          disabled={irDisabled}
          onChange={() => setFormat(FORMAT.IR)}
        />
      </div>
    </Expand>
  );
};

const SearchFilters = ({
  superTag,
  superMake,
  superPlatform,
  selectArchitecture,
  flowLockedArchitecture,
  defaultArchitecture,
  lockCalibrated,
  technicalDisabled,
  formatLocked,
  formatFilterHidden,
  selectedGears,
  mobileOpen,
  onMobileOpenChange,
  hasUserAppliedFilters,
}: {
  superTag?: string;
  superMake?: string;
  superPlatform?: FORMAT;
  selectArchitecture?: ArchitectureFilter;
  flowLockedArchitecture?: ArchitectureFilter;
  /** Implicit default when nothing is selected (A2 on core, undefined for legacy partners). */
  defaultArchitecture?: ArchitectureFilter;
  /** Partner-locked calibrated filter (OAuth flow): forces it on, hides the toggle. */
  lockCalibrated?: boolean;
  technicalDisabled: boolean;
  /** Format is fixed by the flow (OAuth / embed) — render the drawer disabled. */
  formatLocked?: boolean;
  /** Locked gear supports only one format (IR-only category pages) — omit the drawer. */
  formatFilterHidden?: boolean;
  /** Effective selected gears — drives which Format checkboxes are disabled. */
  selectedGears?: GEAR[];
  mobileOpen: boolean;
  onMobileOpenChange: (open: boolean) => void;
  hasUserAppliedFilters: boolean;
}) => {
  const t = useTranslations('search.filters');
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  // Each section's expanded state lives here so Reset can collapse them all.
  const urlTags = searchParams.get('tags');
  const urlMakes = searchParams.get('makes');
  const urlCreators = searchParams.get('creators');
  const urlArchitecture = searchParams.get('architecture');
  const urlSizes = searchParams.get('sizes');
  const urlCalibrated = searchParams.get('calibrated') === 'true';
  const urlVerified = searchParams.get('verified') === 'true';
  const urlFormat = searchParams.get('format');
  const [formatExpanded, setFormatExpanded] = useState(urlFormat !== null);
  const [tagExpanded, setTagExpanded] = useState(urlTags !== null);
  const [makeExpanded, setMakeExpanded] = useState(urlMakes !== null);
  const [creatorExpanded, setCreatorExpanded] = useState(urlCreators !== null || urlVerified);
  const [technicalExpanded, setTechnicalExpanded] = useState(
    !!urlArchitecture || urlCalibrated || urlSizes !== null || !!lockCalibrated
  );

  const clearFilters = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('tags');
    newParams.delete('makes');
    newParams.delete('creators');
    newParams.delete('verified');
    newParams.delete('gears');
    newParams.delete('calibrated');
    newParams.delete('sizes');
    newParams.delete('architecture');
    newParams.delete('format');
    newParams.delete('platform');
    router.push(`${pathname}?${orderSearchParams(newParams)}`, { scroll: false });
    setFormatExpanded(false);
    setTagExpanded(false);
    setMakeExpanded(false);
    setCreatorExpanded(false);
    setTechnicalExpanded(false);
  };

  return (
    <div className="flex flex-col gap-4 w-full flex-1 md:w-[300px] md:flex-initial">
      {/* Desktop header - hidden on mobile */}
      <div className="hidden md:flex flex-row justify-between items-center gap-4 h-[34px]">
        <div className="flex flex-row items-center gap-4">
          <FilterIcon />
          <h3 className="font-semibold leading-2" style={{ lineHeight: '17px' }}>
            {t('title')}
          </h3>
          {hasUserAppliedFilters && <span className="bg-white rounded-full h-2 w-2"></span>}
        </div>
        {hasUserAppliedFilters && (
          <button
            onClick={clearFilters}
            className="text-white flex gap-2 items-center border border-white rounded-lg px-3 py-1"
          >
            {t('reset')}
          </button>
        )}
      </div>

      {/* Filters: desktop sidebar + mobile full-page sheet */}
      <FilterSheet
        mobileOpen={mobileOpen}
        onMobileOpenChange={onMobileOpenChange}
        onReset={clearFilters}
        hasUserAppliedFilters={hasUserAppliedFilters}
      >
        <div className="flex flex-col gap-4">
          {!formatFilterHidden && (
            <FormatFilter
              isExpanded={formatExpanded}
              onExpandChange={setFormatExpanded}
              disabled={formatLocked}
              lockedFormat={superPlatform}
              selectedGears={selectedGears}
            />
          )}
          {!superTag && (
            <TagFilter
              superPlatform={superPlatform}
              selectArchitecture={selectArchitecture}
              lockCalibrated={lockCalibrated}
              isExpanded={tagExpanded}
              onExpandChange={setTagExpanded}
            />
          )}
          {!superMake && (
            <MakeFilter
              superPlatform={superPlatform}
              selectArchitecture={selectArchitecture}
              lockCalibrated={lockCalibrated}
              isExpanded={makeExpanded}
              onExpandChange={setMakeExpanded}
            />
          )}
          <CreatorFilter
            superPlatform={superPlatform}
            selectArchitecture={selectArchitecture}
            lockCalibrated={lockCalibrated}
            isExpanded={creatorExpanded}
            onExpandChange={setCreatorExpanded}
          />
          {/* Technical is disabled (greyed out) when Format is IR — IR
              has no NAM-specific technical attributes. Same behavior as the
              legacy IR-gear selection. */}
          <TechnicalFilter
            selectArchitecture={selectArchitecture}
            flowLockedArchitecture={flowLockedArchitecture}
            defaultArchitecture={defaultArchitecture}
            lockCalibrated={lockCalibrated}
            isDisabled={technicalDisabled}
            isExpanded={technicalExpanded}
            onExpandChange={setTechnicalExpanded}
          />
        </div>
      </FilterSheet>
    </div>
  );
};

export default SearchFilters;
